import React from 'react';
import { classNames } from 'primereact/utils';
import { Button, ButtonProps } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';
import { FormField } from '../../FormField/FormField';
import { UserDisplay } from '../../UserDisplay/UserDisplay';
import { ExternalUsersDropdownButton } from '../../ExternalUsersDropdownButton/ExternalUsersDropdownButton';
import { FormElementError } from '../../FormElementError/FormElementError';
import { ReservationType } from '../../../types/reservationType';
import { ExternalUserShort } from '../../../types/externalUser';
import { Routine } from '../../../types/Fisiofit/routine';
import { ExternalUserWithCancellation, ReservationFormFieldProps } from '../types';

interface Props extends ReservationFormFieldProps {
  reservationType: ReservationType;
  usersToShow: ExternalUserShort[];
  userArrayLength: number;
  canceledUsers: ExternalUserWithCancellation[];
  isEditing: boolean;
  isModifying: boolean;
  isPast: boolean;
  showRequiredFields: boolean;
  showQueuedUsers: boolean;
  setShowQueuedUsers: (show: boolean) => void;
  hasRoutinesPermission: boolean;
  selectedUserForRoutine: ExternalUserShort | null;
  setSelectedUserForRoutine: (user: ExternalUserShort) => void;
  isLoadingRoutine: boolean;
  userRoutine: Routine | undefined;
  getGroupExternalUserButtonProps: (externalUser: ExternalUserShort, isConfirmed: boolean) => ButtonProps | undefined;
  renderUserAttendance: (user: ExternalUserShort) => React.ReactNode;
}

/** The clients, free places and waiting list of a group reservation. */
export const GroupClientsField = ({
  form,
  setFormFields,
  validationErrors,
  reservationType,
  usersToShow,
  userArrayLength,
  canceledUsers,
  isEditing,
  isModifying,
  isPast,
  showRequiredFields,
  showQueuedUsers,
  setShowQueuedUsers,
  hasRoutinesPermission,
  selectedUserForRoutine,
  setSelectedUserForRoutine,
  isLoadingRoutine,
  userRoutine,
  getGroupExternalUserButtonProps,
  renderUserAttendance,
}: Props) => {
  return (
    <FormField
      labelTitle='Clientes'
      fullWidth
      error={validationErrors?.externalUsers}
      dontShowErrorMessage
      required={showRequiredFields}
    >
      <div
        className={classNames('group-reservation-clients', {
          'invalid-form-field': validationErrors?.externalUsers,
        })}
      >
        {!usersToShow.length && reservationType?.hasNoGroupSizeLimit
          ? null
          : Array.from({ length: userArrayLength }).map((_, index) => {
              const user = usersToShow[index];
              return user ? (
                <div key={user.id} className='user-display-container'>
                  <div className='user-info'>
                    <UserDisplay
                      name={user.name}
                      surnames={user.surnames}
                      avatarUrl={user.avatarUrl}
                      bookedFromApp={user.bookedFromApp}
                      buttonProps={getGroupExternalUserButtonProps(
                        user,
                        form?.confirmedExternalUsers?.some((u) => u.id === user.id) ?? false,
                      )}
                      {...(!isEditing && { navigateTo: `/users/profile/${user.id}` })}
                    />
                    {/* Icono para ver rutina del usuario */}
                    {hasRoutinesPermission && (
                      <>
                        <Button
                          icon='pi pi-wave-pulse'
                          className='user-routine-button'
                          rounded
                          text
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedUserForRoutine(user);
                          }}
                          loading={isLoadingRoutine && selectedUserForRoutine?.id === user.id}
                          disabled={isLoadingRoutine}
                          tooltip='Ver rutina'
                          tooltipOptions={{ position: 'top' }}
                          {...(selectedUserForRoutine?.id === user.id &&
                            !isLoadingRoutine &&
                            !userRoutine && {
                              severity: 'danger',
                              disabled: true,
                            })}
                        />
                        {selectedUserForRoutine?.id === user.id && !isLoadingRoutine && !userRoutine && (
                          <small className='user-routine-button-error-text'>Sin rutina</small>
                        )}
                      </>
                    )}
                    {/* Icono para lesión en curso */}
                    {user.hasOngoingInjury && (
                      <>
                        <i
                          id={`ongoing-injury-${user.id}`}
                          className={classNames('pi', PrimeIcons.PLUS_CIRCLE, 'user-ongoing-injury-icon')}
                        />
                        <Tooltip target={`#ongoing-injury-${user.id}`} position='top'>
                          Lesión en curso
                        </Tooltip>
                      </>
                    )}
                  </div>
                  {renderUserAttendance(user)}
                  {form?.usersBeforeSave?.some((u) => u.id === user.id) && <span className='pending-user-note'>no guardado</span>}
                </div>
              ) : reservationType.hasNoGroupSizeLimit ? null : (
                <UserDisplay key={index} fullName='Plaza libre' isPlaceholder />
              );
            })}
        <FormElementError position={'absolute'}>{validationErrors?.externalUsers?.toString()}</FormElementError>
      </div>
      {isModifying && !isPast && (
        <div className='button-container'>
          <ExternalUsersDropdownButton
            filterOnlyInvitedToApp={reservationType.externalUserCanCreate}
            onSelect={(user) => {
              setFormFields({ usersBeforeSave: [...(form?.usersBeforeSave ?? []), user] });
            }}
            excludedUserIds={[
              ...(form?.externalUsers ?? []),
              ...(form?.confirmedExternalUsers ?? []),
              ...(form?.queuedExternalUsers ?? []),
              ...(canceledUsers ?? []),
              ...(form?.usersBeforeSave ?? []),
            ].map((user) => user.id)}
          />
        </div>
      )}
      {form?.queuedExternalUsers && form.queuedExternalUsers.length > 0 && (
        <div className='queued-users-section'>
          <div
            className={classNames('queued-users-header', { expanded: showQueuedUsers })}
            onClick={() => setShowQueuedUsers(!showQueuedUsers)}
          >
            <i className='pi pi-chevron-right' />
            <span>
              Lista de espera ({form.queuedExternalUsers.length} {form.queuedExternalUsers.length === 1 ? 'usuario' : 'usuarios'})
            </span>
          </div>
          {showQueuedUsers && (
            <div className='queued-users-list'>
              {form.queuedExternalUsers.map((user) => (
                <UserDisplay
                  key={user.id}
                  name={user.name}
                  surnames={user.surnames}
                  avatarUrl={user.avatarUrl}
                  bookedFromApp={user.bookedFromApp}
                  {...(!isEditing && { navigateTo: `/users/profile/${user.id}` })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </FormField>
  );
};
